import { Card, CardContent } from "@/components/ui/card";
import { ContactForm } from "@/components/ContactForm";
import { Phone, Mail, Clock, MapPin, Facebook, Linkedin, Instagram } from "lucide-react";

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-[#0F172A] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block text-[#F59E0B] font-medium text-sm uppercase tracking-wider mb-4">
            Get in Touch
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
            Contact Us
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            Have a question or need more information? We're here to help.
            Reach out to our team and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 lg:py-24 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 lg:p-10">
                  <h2 className="font-heading text-2xl font-bold text-[#0F172A] uppercase mb-8">
                    Send Us a Message
                  </h2>
                  <ContactForm />
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Details */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase mb-6">
                    Contact Details
                  </h3>
                  <ul className="space-y-5">
                    <li>
                      <p className="text-sm text-slate-500 mb-1">Phone</p>
                      <a href="tel:+61435825379" className="flex items-center gap-3 text-[#0F172A] font-medium hover:text-[#F59E0B] transition-colors">
                        <Phone className="w-5 h-5 text-[#F59E0B]" />
                        <span>0435 825 379</span>
                      </a>
                    </li>
                    <li>
                      <p className="text-sm text-slate-500 mb-1">Email</p>
                      <a href="mailto:danny@gappro.com.au" className="flex items-center gap-3 text-[#0F172A] font-medium hover:text-[#F59E0B] transition-colors">
                        <Mail className="w-5 h-5 text-[#F59E0B]" />
                        <span>danny@gappro.com.au</span>
                      </a>
                    </li>
                    <li>
                      <p className="text-sm text-slate-500 mb-1">Location</p>
                      <div className="flex items-start gap-3 text-[#0F172A]">
                        <MapPin className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                        <span>Gold Coast, Queensland<br />Australia</span>
                      </div>
                    </li>
                    <li>
                      <p className="text-sm text-slate-500 mb-1">Business Hours</p>
                      <div className="flex items-start gap-3 text-[#0F172A]">
                        <Clock className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                        <div>
                          <p>Monday - Friday: 6:30am - 5pm</p>
                        </div>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Social Media */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-bold text-[#0F172A] uppercase mb-6">
                    Follow Us
                  </h3>
                  <div className="flex gap-4">
                    <a 
                      href="#" 
                      className="w-12 h-12 bg-[#0F172A] hover:bg-[#F59E0B] rounded-lg flex items-center justify-center transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5 text-white" />
                    </a>
                    <a 
                      href="#" 
                      className="w-12 h-12 bg-[#0F172A] hover:bg-[#F59E0B] rounded-lg flex items-center justify-center transition-colors"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5 text-white" />
                    </a>
                    <a 
                      href="#" 
                      className="w-12 h-12 bg-[#0F172A] hover:bg-[#F59E0B] rounded-lg flex items-center justify-center transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5 text-white" />
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Map Placeholder */}
              <Card className="border-0 shadow-lg overflow-hidden">
                <div className="relative h-64">
                  <img
                    src="https://images.unsplash.com/photo-1649510777971-eef1cdb3e6d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxnb2xkJTIwY29hc3QlMjBhdXN0cmFsaWElMjBza3lsaW5lJTIwbW9kZXJuJTIwaGlnaCUyMHJpc2UlMjBidWlsZGluZ3xlbnwwfHx8fDE3NzE0Nzc5Njd8MA&ixlib=rb-4.1.0&q=85"
                    alt="Gold Coast"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-[#0F172A]/60 flex items-center justify-center">
                    <div className="text-center text-white">
                      <MapPin className="w-8 h-8 text-[#F59E0B] mx-auto mb-2" />
                      <p className="font-heading font-bold uppercase">Gold Coast, QLD</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
